import { PartnerSettingValue } from '@provision/entity-types';
import { PostHog, PostHogConfig } from 'posthog-js';
import { PostHogProvider, usePostHog } from 'posthog-js/react';
import { ReactNode, useEffect, useMemo } from 'react';
import { config } from 'src/core/config';
import { PartnerSetting, useCurrentUserProfile, usePartnerSetting } from 'src/graphql';
import { MappedUserData } from 'src/store/current-user';
import { isLocalUrl } from 'src/utils/routing';

export const OVPostHogProvider = ({ children }: { children: ReactNode }) => {
  const { data: postHogOn } = usePartnerSetting(PartnerSetting.PostHogOn);

  const { user } = useCurrentUserProfile();

  const posthog = usePostHog();

  useEffect(() => {
    checkTracking({
      ph: posthog,
      user,
      postHogOn,
      location: window.location,
    });
  }, [postHogOn, posthog, user]);

  const options: Partial<PostHogConfig> = useMemo(
    () => ({
      api_host: config.postHogHost,
      person_profiles: 'always',
      capture_pageview: false,
      loaded: (ph) => {
        ph.register({ app: 'portal' });
        checkTracking({ ph, postHogOn, user, location: window.location });
      },
    }),
    [postHogOn, user],
  );

  return (
    <PostHogProvider apiKey={config.postHogKey} options={options}>
      {children}
    </PostHogProvider>
  );
};

function checkTracking({
  ph,
  postHogOn,
  user,
  location,
}: {
  ph: PostHog;
  postHogOn?: Partial<PartnerSettingValue>;
  user: MappedUserData | null;
  location: Location;
}) {
  if (postHogOn?.value?.toLowerCase() !== 'true' || isLocalUrl(location.hostname)) {
    ph.config.autocapture = false;
    ph.opt_out_capturing(); // opts a user out of event capture
    ph.stopSessionRecording();
  } else {
    ph.config.autocapture = true;
    ph.identify(user?.userId, user ? { ...user, app: 'portal' } : { app: 'portal' });
    ph.opt_in_capturing();
    ph.startSessionRecording();
  }
}
